import React, { ForwardRefExoticComponent, SVGProps } from 'react'

import { Table } from '@factorialco/f0-react/icons/app'
import { FormsElementTypeEnum } from 'generated/resources/datatable.graphql'

import i18n from 'modules/core/lib/i18n'

import { OptionElementQuestion } from 'modules/forms/components/QuestionComponent/OptionElementQuestion'
import ChoiceElementEditor from 'modules/forms/components/editor/ChoiceElementEditor/ChoiceElementEditor'
import { Element } from 'modules/forms/lib/Element'
import { Answer, Options, elementParam } from 'modules/forms/lib/types'

/**
 * DropdownMultiElement class for elements with multiple selectable options via dropdown.
 * Extends the base Element class.
 */
export class DropdownMultiElement extends Element {
  static create({
    label,
    options = {},
  }: {
    label: string
    options: Options
  }): DropdownMultiElement {
    const el = new DropdownMultiElement({
      label,
      description: options.description,
      mandatory: options.mandatory || options.locked,
    })
    if (options.locked) {
      el.lock()
    }

    return el
  }

  getName(): string {
    return i18n.t('forms.modals.elements.dataset_multiple_choice')
  }

  getType(): FormsElementTypeEnum {
    return 'dropdownmulti'
  }

  getIcon(): ForwardRefExoticComponent<SVGProps<SVGSVGElement>> {
    return Table
  }

  validate(validateCorrectOption: boolean): Record<string, string> {
    const errors = super.validate(validateCorrectOption)

    const options = this.options
    if (options.length < 2) {
      errors.options = i18n.t('forms.editor.validation.min_two_options')
    } else if (options.some((option) => !option.label.trim())) {
      errors.options = i18n.t('forms.editor.validation.option_label_required')
    } else if (validateCorrectOption && !options.some((option) => option.isCorrect)) {
      errors.options = i18n.t('forms.editor.validation.no_correct_option')
    }

    return errors
  }

  getEditorComponent(
    onChange: (element: Element) => void,
    enableCorrectConfiguration: boolean
  ): React.ReactNode {
    return (
      <ChoiceElementEditor
        element={this}
        onChange={onChange}
        enableCorrectConfiguration={enableCorrectConfiguration}
      />
    )
  }

  getPreviewComponent(disabled?: boolean, showCorrectAnswerIcons?: boolean): React.ReactNode {
    return (
      <OptionElementQuestion
        element={this}
        isSingleChoice={false}
        disabled={disabled}
        showCorrectAnswerIcons={showCorrectAnswerIcons}
      />
    )
  }

  getQuestionComponent(onChange: (answer: Answer) => void): React.ReactNode {
    return (
      <OptionElementQuestion
        element={this}
        isSingleChoice={false}
        onChange={onChange}
        showCorrectAnswerIcons={false}
      />
    )
  }

  configureParams(): elementParam[] {
    return [Element.booleanParam('randomizeOptions', 'Randomize')]
  }
}
